import React from 'react';
import { motion } from 'framer-motion';
import { Book, Shield, Brain, Target, Users, Rocket, Code, Search } from 'lucide-react';

export default function Documentation() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Dokumentacja Kariera z AI
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Kompleksowy przewodnik po platformie wykorzystującej sztuczną inteligencję do planowania kariery
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Technologia i AI */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <Brain className="w-12 h-12 text-purple-600 mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Technologia AI</h2>
            <div className="prose prose-purple">
              <p>
                Kariera z AI wykorzystuje zaawansowane modele sztucznej inteligencji GPT-4,
                zapewniając precyzyjną analizę predyspozycji zawodowych i personalizowane rekomendacje kariery.
                Nasza platforma łączy:
              </p>
              <ul>
                <li>Zaawansowane algorytmy uczenia maszynowego</li>
                <li>Analiza big data rynku pracy</li>
                <li>Przetwarzanie języka naturalnego</li>
                <li>Adaptacyjne systemy rekomendacji</li>
              </ul>
            </div>
          </motion.div>

          {/* Metodologia */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <Target className="w-12 h-12 text-purple-600 mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Metodologia</h2>
            <div className="prose prose-purple">
              <p>
                Nasze podejście do doradztwa kariery opiera się na kompleksowej analizie:
              </p>
              <ul>
                <li>Predyspozycji osobowościowych</li>
                <li>Umiejętności technicznych i miękkich</li>
                <li>Trendów rynkowych i prognoz zatrudnienia</li>
                <li>Ścieżek rozwoju zawodowego</li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Sekcja SEO */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Doradztwo Kariery w Erze AI</h2>
          <div className="prose prose-purple max-w-none">
            <p>
              Kariera z AI to innowacyjna platforma doradztwa zawodowego, która wykorzystuje
              sztuczną inteligencję do analizy predyspozycji i planowania kariery. Nasza platforma
              jest odpowiedzią na dynamicznie zmieniający się rynek pracy w Polsce i na świecie.
            </p>
            
            <h3>Dlaczego AI w doradztwie kariery?</h3>
            <p>
              Sztuczna inteligencja wprowadza nową jakość do procesu planowania kariery:
            </p>
            <ul>
              <li>Analiza milionów danych o rynku pracy w czasie rzeczywistym</li>
              <li>Personalizacja rekomendacji w oparciu o indywidualne predyspozycje</li>
              <li>Przewidywanie trendów i zawodów przyszłości</li>
              <li>Ciągła aktualizacja wiedzy o wymaganiach pracodawców</li>
            </ul>

            <h3>Zawody Przyszłości w Polsce</h3>
            <p>
              Nasza platforma specjalizuje się w identyfikacji i analizie zawodów przyszłości 
              na polskim rynku pracy. Szczególną uwagę poświęcamy takim obszarom jak:
            </p>
            <ul>
              <li>Sztuczna inteligencja i uczenie maszynowe</li>
              <li>Cyberbezpieczeństwo</li>
              <li>Analiza danych</li>
              <li>Transformacja cyfrowa</li>
              <li>Zrównoważony rozwój</li>
            </ul>

            <h3>Rozwój Kariery w Erze Cyfrowej</h3>
            <p>
              Oferujemy kompleksowe wsparcie w rozwoju kariery, dostosowane do wymagań ery cyfrowej:
            </p>
            <ul>
              <li>Spersonalizowane ścieżki rozwoju zawodowego</li>
              <li>Rekomendacje szkoleń i certyfikacji</li>
              <li>Analiza trendów rynkowych</li>
              <li>Dopasowanie do kultury organizacyjnej</li>
            </ul>
          </div>
        </div>

        {/* Funkcjonalności */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <Search className="w-8 h-8 text-purple-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Analiza Predyspozycji</h3>
            <p className="text-gray-600">
              Zaawansowana analiza AI Twoich umiejętności, zainteresowań i celów zawodowych.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <Rocket className="w-8 h-8 text-purple-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Plan Rozwoju</h3>
            <p className="text-gray-600">
              Spersonalizowany plan rozwoju kariery z konkretnymi krokami i celami.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <Code className="w-8 h-8 text-purple-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Rekomendacje</h3>
            <p className="text-gray-600">
              Dopasowane oferty pracy i kursy rozwijające kluczowe kompetencje.
            </p>
          </motion.div>
        </div>

        {/* Rich SEO Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Przyszłość Pracy w Polsce</h2>
          <div className="prose prose-purple max-w-none">
            <p>
              Kariera z AI aktywnie analizuje i prognozuje zmiany na polskim rynku pracy.
              Nasza platforma wykorzystuje zaawansowane algorytmy AI do identyfikacji trendów
              i wspierania użytkowników w podejmowaniu świadomych decyzji zawodowych.
            </p>

            <h3>Kluczowe Trendy na Rynku Pracy</h3>
            <ul>
              <li>Automatyzacja i robotyzacja procesów</li>
              <li>Rozwój pracy zdalnej i hybrydowej</li>
              <li>Wzrost znaczenia umiejętności cyfrowych</li>
              <li>Transformacja tradycyjnych zawodów</li>
              <li>Nowe specjalizacje w IT i technologii</li>
            </ul>

            <h3>Wsparcie w Rozwoju Kariery</h3>
            <p>
              Oferujemy kompleksowe wsparcie w planowaniu i rozwoju kariery:
            </p>
            <ul>
              <li>Analiza predyspozycji zawodowych</li>
              <li>Rekomendacje ścieżek kariery</li>
              <li>Dopasowane oferty pracy</li>
              <li>Spersonalizowany plan rozwoju</li>
              <li>Monitoring postępów</li>
            </ul>

            <h3>Technologia w Służbie Kariery</h3>
            <p>
              Wykorzystujemy najnowocześniejsze technologie:
            </p>
            <ul>
              <li>Sztuczna inteligencja GPT-4</li>
              <li>Zaawansowane algorytmy uczenia maszynowego</li>
              <li>Analiza big data</li>
              <li>Systemy rekomendacji</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
